import { useEffect, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import toast from 'react-hot-toast';
import api from '../services/api';

interface Plan {
  id: number;
  code: string;
  name: string;
  priceMonthly: number;
  priceYearly: number;
  maxVkAccounts: number;
  maxConfigs: number;
  allowedTools: string[];
  schedulerIntervalMin: number;
  historyRetentionDays: number;
  isPopular: boolean;
}

interface SubscriptionInfo {
  subscription: {
    status: string;
    isTrial: boolean;
    periodEnd: string;
  } | null;
  plan: Plan | null;
  usage: { vkAccounts: number; configs: number };
  daysLeft?: number;
  paymentsEnabled?: boolean;
}

const TOOL_LABELS: Record<string, string> = {
  liker: 'Автолайкер',
  commenter: 'Автокомментер',
  subscriber: 'Автоподписчик',
};

function intervalLabel(min: number): string {
  if (min < 60) return `каждые ${min} мин`;
  if (min === 60) return 'каждый час';
  return `каждые ${min / 60} ч`;
}

function retentionLabel(days: number): string {
  if (days >= 3650) return 'История без ограничений';
  return `История действий ${days} дней`;
}

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<'month' | 'year'>('month');
  const [payingPlan, setPayingPlan] = useState<string | null>(null);

  useEffect(() => {
    // Robokassa Success/Fail URLs must be parameter-free with GET, so the
    // outcome is carried by the path (/billing/success | /billing/fail).
    // The legacy ?payment= form is still honoured for backward compat.
    const outcome = location.pathname.endsWith('/success')
      ? 'success'
      : location.pathname.endsWith('/fail')
      ? 'fail'
      : searchParams.get('payment');

    if (outcome === 'success') {
      toast.success('Оплата прошла! Подписка активируется в течение минуты.');
      navigate('/billing', { replace: true });
    } else if (outcome === 'fail') {
      toast.error('Оплата не прошла. Попробуйте ещё раз.');
      navigate('/billing', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    Promise.all([api.get('/billing/plans'), api.get('/billing/subscription')])
      .then(([plansRes, subRes]) => {
        setPlans(plansRes.data.data.plans);
        setInfo(subRes.data.data);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handlePay = async (planCode: string) => {
    try {
      setPayingPlan(planCode);
      const res = await api.post('/billing/checkout', { planCode, period });
      window.location.href = res.data.data.paymentUrl;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось создать платёж');
      setPayingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const current = info?.plan;
  const sub = info?.subscription;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Подписка
      </Typography>

      {sub && current ? (
        <Alert severity={sub.isTrial ? 'info' : 'success'} sx={{ mb: 3 }}>
          {sub.isTrial ? (
            <>
              Пробный период тарифа <strong>«{current.name}»</strong> — осталось{' '}
              {info?.daysLeft} дн. (до{' '}
              {new Date(sub.periodEnd).toLocaleDateString('ru-RU')}). После окончания
              правила будут остановлены до оформления подписки.
            </>
          ) : (
            <>
              Активная подписка: тариф <strong>«{current.name}»</strong> до{' '}
              {new Date(sub.periodEnd).toLocaleDateString('ru-RU')} ({info?.daysLeft} дн.).
            </>
          )}{' '}
          Используется: {info?.usage.vkAccounts}/{current.maxVkAccounts} VK-аккаунтов,{' '}
          {info?.usage.configs}/{current.maxConfigs} правил.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 3 }}>
          У вас нет активной подписки — правила не запускаются. Выберите тариф ниже.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, v) => v && setPeriod(v)}
        >
          <ToggleButton value="month">Помесячно</ToggleButton>
          <ToggleButton value="year">На год (−20%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {plans.map((plan) => {
          const isCurrent = current?.code === plan.code;
          return (
            <Grid item xs={12} md={4} key={plan.code}>
              <Card
                variant={plan.isPopular ? 'elevation' : 'outlined'}
                elevation={plan.isPopular ? 6 : 0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isCurrent ? 'primary.main' : undefined,
                  borderWidth: isCurrent ? 2 : undefined,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h5">{plan.name}</Typography>
                    {plan.isPopular && <Chip label="Популярный" color="primary" size="small" />}
                    {isCurrent && <Chip label="Ваш тариф" color="success" size="small" />}
                  </Box>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>
                    {period === 'year' ? plan.priceYearly : plan.priceMonthly} ₽
                    <Typography component="span" variant="body2" color="text.secondary">
                      /{period === 'year' ? 'год' : 'мес'}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {period === 'year'
                      ? `≈ ${Math.round(plan.priceYearly / 12)} ₽/мес — выгода ${plan.priceMonthly * 12 - plan.priceYearly} ₽ в год`
                      : `или ${plan.priceYearly} ₽/год (−20%)`}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={`VK-аккаунтов: ${plan.maxVkAccounts}`} />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={`Правил: ${plan.maxConfigs}`} />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={plan.allowedTools.map((t) => TOOL_LABELS[t] || t).join(', ')}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={`Проверка ${intervalLabel(plan.schedulerIntervalMin)}`} />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={retentionLabel(plan.historyRetentionDays)} />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {info?.paymentsEnabled ? (
                    <Button
                      fullWidth
                      variant={plan.isPopular ? 'contained' : 'outlined'}
                      disabled={payingPlan !== null}
                      onClick={() => handlePay(plan.code)}
                    >
                      {payingPlan === plan.code
                        ? 'Переход к оплате...'
                        : `Оплатить ${period === 'year' ? plan.priceYearly : plan.priceMonthly} ₽`}
                    </Button>
                  ) : (
                    <Button fullWidth variant={plan.isPopular ? 'contained' : 'outlined'} disabled>
                      {isCurrent ? 'Текущий тариф' : 'Оплата скоро будет доступна'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {info?.paymentsEnabled
          ? 'Оплата банковской картой через Робокассу; чек придёт на ваш email. '
          : 'Оплата картой через Робокассу подключается — кнопки станут активны после запуска приёма платежей. '}
        Дневные лимиты действий на VK-аккаунт задаются настройками аккаунта и не зависят
        от тарифа (это защита от блокировок VK).
      </Typography>
    </Box>
  );
}
